import React, { useEffect, useRef, useState } from 'react'
import { CSSTransition, SwitchTransition } from 'react-transition-group'
import { Box } from '@mui/material'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { signOut, useSession } from 'next-auth/react'

import InfoBar from '#/app/layout/ui/info-bar'
import { useAppDispatch, useAppSelector } from '#/app/store/store'
import { getUserBalance } from '#/entities/balance'
import { getAllInfo } from '#/entities/user-account'
import { ChangePasswordPlate } from '#/features/change-password-corp'
import { ErrorReportPlate } from '#/features/error-report'
import { change } from '#/features/pending'
import { SlowLoading } from '#/features/slow-loading'
import { Loader } from '#/shared'
import { getDeviceType } from '#/shared/lib/helpers'
import { MainMenuMobile } from '#/widgets/main-menu'
import { SideMenu } from '#/widgets/side-menu'

import { LayoutProps } from '../types'

import styles from './default.module.scss'

export const Layout: React.FC<LayoutProps> = ({ children, titlePage, isLoader, height, device = getDeviceType() }) => {
	const dispatch = useAppDispatch()

	const meStatus = useAppSelector((state) => state.user.status)

	const { userLoading } = useAppSelector((state) => state.loading)

	const desktop = device === 'desktop'


	const { data, status } = useSession()

	const { push } = useRouter()

	function logout() {
		signOut()
		push('/login')
	}

	async function getUserData(access: string) {
		await dispatch(getUserBalance(access))
		await dispatch(getAllInfo(access))
		dispatch(change(false))
	}

	useEffect(() => {
		if (meStatus === 'failed') {
			signOut()
			push('/login')
		}
	}, [meStatus])

	useEffect(() => {
		if (!data) return

		if (!data.user || !data.access || !data.refresh) logout()
	}, [data])

	useEffect(() => {
		if (!data) return
		getUserData(data.access)
	}, [data])

	const [requestDelay, setRequestDelay] = useState(false)

	const transitionRef = useRef(null)

	useEffect(() => {
		setTimeout(() => setRequestDelay(true), 5000)
	}, [])

	if (userLoading || isLoader || status === 'loading') {
		return (
			<SwitchTransition>
				<CSSTransition
					nodeRef={transitionRef}
					key={requestDelay ? 'button' : 'loader'}
					timeout={400}
					classNames='my-node'
					in={requestDelay}
				>
					<div ref={transitionRef} className={styles.loading}>
						{requestDelay ? <SlowLoading /> : <Loader size={50} />}
					</div>
				</CSSTransition>
			</SwitchTransition>
		)
	}

	return (
		<>
			<Head>
				{titlePage && <title>{titlePage}</title>}
				<meta name='AIR' />
				<meta name='viewport' content='width=device-width, initial-scale=1' />
				<link rel='icon' href='/logo.svg' />
				<link rel='apple-touch-icon' href='/logo180.png'></link>
				<link rel='manifest' href='/manifest.json' />
			</Head>

			<Box
				id={'layout'}
				sx={{
					width: '100%',
					height: desktop ? (height ? height : '100%') : '100dvh',
					padding: desktop ? '0px' : '10px',
					overflow: desktop ? undefined : 'hidden',
				}}
			>
				<Box
					sx={{
						padding: desktop ? '0px' : '1px',
						height: desktop ? undefined : '100%',
						minHeight: 0,
					}}
				>
					{desktop ? (
						<Box display='flex' justifyContent='center'>
							<SideMenu />
							<Box
								width='100%'
								sx={{
									padding: '5px 0px 0px 70px',
									marginLeft: '60px',
									overflowX: 'hidden',
									minHeight: '100dvh',
								}}
							>
								<InfoBar device={device} token={''} />
								<Box sx={{ paddingBottom: '15px' }} width='100%'>
									{children}
								</Box>
							</Box>
						</Box>
					) : (
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								height: '100%',
								minHeight: 0,
								overflow: 'hidden',
							}}
						>
							<MainMenuMobile />
							<Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>{children}</Box>
						</Box>
					)}
				</Box>

				{/* Дефолтные модалки */}
				<ChangePasswordPlate />
				<ErrorReportPlate />
			</Box>
		</>
	)
}
